import { LayoutPanelLeft, LayoutPanelTop, Monitor, Moon, Sun } from 'lucide-react'

import { Button } from '@/components/ui/button'

import type { TextLayout, TextSize, TextTheme } from '../types'

interface TextContentViewerToolbarProps {
  theme: TextTheme
  resolvedTheme: 'light' | 'dark'
  setTheme: (t: TextTheme) => void
  textSize: TextSize
  setTextSize: (s: TextSize) => void
  layout: TextLayout
  setLayout: (l: TextLayout) => void
}

export function TextContentViewerToolbar({
  theme,
  resolvedTheme,
  setTheme,
  textSize,
  setTextSize,
  layout,
  setLayout,
}: TextContentViewerToolbarProps) {
  const toolbarClass =
    resolvedTheme === 'dark'
      ? 'sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b-2 border-zinc-600 bg-zinc-800 px-4 py-2.5 text-zinc-100 shadow-lg shadow-black/20'
      : 'sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60'

  return (
    <div className={toolbarClass}>
      <div className="flex items-center gap-1">
        <span
          className={`mr-2 text-xs font-medium ${resolvedTheme === 'dark' ? 'text-zinc-200' : 'text-muted-foreground'}`}
        >
          Theme
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Light"
          onClick={() => setTheme('light')}
          className={
            theme === 'light'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          <Sun className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dark"
          onClick={() => setTheme('dark')}
          className={
            theme === 'dark'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          <Moon className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="System"
          onClick={() => setTheme('system')}
          className={
            theme === 'system'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          <Monitor className="size-4" />
        </Button>
      </div>
      <div className={`h-4 w-px ${resolvedTheme === 'dark' ? 'bg-zinc-500' : 'bg-border'}`} />
      <div className="flex items-center gap-1">
        <span
          className={`mr-2 text-xs font-medium ${resolvedTheme === 'dark' ? 'text-zinc-200' : 'text-muted-foreground'}`}
        >
          Size
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextSize('sm')}
          className={
            textSize === 'sm'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          S
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextSize('base')}
          className={
            textSize === 'base'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          M
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setTextSize('lg')}
          className={
            textSize === 'lg'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          L
        </Button>
      </div>
      <div className={`h-4 w-px ${resolvedTheme === 'dark' ? 'bg-zinc-500' : 'bg-border'}`} />
      <div className="flex items-center gap-1">
        <span
          className={`mr-2 text-xs font-medium ${resolvedTheme === 'dark' ? 'text-zinc-200' : 'text-muted-foreground'}`}
        >
          Layout
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Single column"
          onClick={() => setLayout('single')}
          className={
            layout === 'single'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          <LayoutPanelTop className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="With sidebar"
          onClick={() => setLayout('sidebar')}
          className={
            layout === 'sidebar'
              ? resolvedTheme === 'dark'
                ? 'bg-zinc-600 text-white hover:bg-zinc-500'
                : 'bg-accent'
              : resolvedTheme === 'dark'
                ? 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                : ''
          }
        >
          <LayoutPanelLeft className="size-4" />
        </Button>
      </div>
    </div>
  )
}
